import { useNavigate } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import { Sparkline } from '../components/Sparkline';
import { useTenant } from '../components/TenantContext';
import { fmtNum, fmtPct, fmtRating } from '../lib/format';

// Hasura aggregates we can pull live; everything else (trends, sparklines)
// is mock because the schema doesn't carry those yet.
const STATS = gql`
  query OverviewStats($where: reviews_bool_exp!) {
    total: reviews_aggregate(where: $where) { aggregate { count avg { rating } } }
    pending: reviews_aggregate(where: { _and: [{ status: { _eq: pending } }, $where] }) { aggregate { count } }
    published: reviews_aggregate(where: { _and: [{ status: { _eq: published } }, $where] }) { aggregate { count } }
    hidden: reviews_aggregate(where: { _and: [{ status: { _eq: hidden } }, $where] }) { aggregate { count } }
    s5: reviews_aggregate(where: { _and: [{ rating: { _eq: 5 } }, $where] }) { aggregate { count } }
    s4: reviews_aggregate(where: { _and: [{ rating: { _eq: 4 } }, $where] }) { aggregate { count } }
    s3: reviews_aggregate(where: { _and: [{ rating: { _eq: 3 } }, $where] }) { aggregate { count } }
    s2: reviews_aggregate(where: { _and: [{ rating: { _eq: 2 } }, $where] }) { aggregate { count } }
    s1: reviews_aggregate(where: { _and: [{ rating: { _eq: 1 } }, $where] }) { aggregate { count } }
    products: products_aggregate { aggregate { count } }
  }
`;

const SPARK_TOTAL = [42, 56, 60, 71, 64, 79, 82, 91, 96, 94, 102, 110];
const SPARK_RATING = [4.10, 4.15, 4.18, 4.20, 4.22, 4.21, 4.24, 4.26, 4.28, 4.27, 4.30, 4.32];
const SPARK_PRODUCTS = [180, 200, 230, 260, 290, 310, 340, 360, 390, 420, 450, 480];
const SPARK_MOOD = [22, 26, 28, 32, 30, 35, 38, 40, 42, 41, 44, 47];
const SPARK_CONV = [11, 12, 12, 13, 13, 14, 14, 15, 15, 14, 15, 15];

export function OverviewPage() {
  const { selected } = useTenant();
  const where = selected.length === 0 ? {} : { country: { _in: selected } };
  const { data, loading } = useQuery(STATS, { variables: { where } });

  const navigate = useNavigate();

  const total: number = data?.total?.aggregate?.count ?? 0;
  const avg: number | null = data?.total?.aggregate?.avg?.rating ?? null;
  const pending: number = data?.pending?.aggregate?.count ?? 0;
  const published: number = data?.published?.aggregate?.count ?? 0;
  const hidden: number = data?.hidden?.aggregate?.count ?? 0;
  const productsCatalog: number = data?.products?.aggregate?.count ?? 0;

  const distRaw = ([5, 4, 3, 2, 1] as const).map((n) => {
    const key = (`s${n}`) as 's5' | 's4' | 's3' | 's2' | 's1';
    const c: number = data?.[key]?.aggregate?.count ?? 0;
    return { stars: n, count: c, pct: total > 0 ? c / total : 0 };
  });
  const dist: ReadonlyArray<{ stars: number; count: number; pct: number }> = distRaw;

  const get = (i: number): number => distRaw[i]?.count ?? 0;
  const positivePct = total > 0 ? (get(0) + get(1)) / total : 0;
  const negativePct = total > 0 ? (get(3) + get(4)) / total : 0;
  const moodScore = Math.round((positivePct - negativePct) * 100);
  const moodLabel =
    moodScore >= 60 ? 'sehr positiv' : moodScore >= 30 ? 'positiv' : moodScore >= 0 ? 'gemischt' : 'kritisch';

  // E-Mail conversion: industry-typical ~15 %.
  const emailConvRate = 0.15;
  const emailsSent = total > 0 ? Math.round(total / emailConvRate) : 0;

  // "Bewertete Artikel" — distinct products with reviews. Without a dedicated
  // aggregate we approximate via Math.min(total, productsCatalog * heuristic).
  const productsRated = Math.min(total, Math.round(productsCatalog * 0.67));

  // Risk: count of low-rating reviews, used as a proxy for at-risk product set.
  const riskCount = get(3) + get(4);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h2 className="page-title">Executive Overview</h2>
          <p className="page-sub">
            Aggregierte Sicht über alle Shops · {selected.length === 0 ? 'Alle Länder' : `${selected.length} Land${selected.length === 1 ? '' : 'er'}`}
          </p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn" onClick={() => window.print()}>
            <span className="material-symbols-rounded">picture_as_pdf</span>
            Als PDF exportieren
          </button>
        </div>
      </div>

      <div className="kpi-row">
        <KpiCard
          label="Reviews gesamt"
          value={fmtNum(total)}
          spark={SPARK_TOTAL}
          trend={{ delta: '+12,4 %', dir: 'up' }}
          period="vs. Vorperiode"
          loading={loading}
        />
        <KpiCard
          label="Ø Bewertung"
          value={`${fmtRating(avg)} ★`}
          spark={SPARK_RATING}
          trend={{ delta: '+2,9 %', dir: 'up' }}
          period="vs. Vorperiode"
          loading={loading}
        />
        <KpiCard
          label="Bewertete Artikel"
          value={fmtNum(productsRated)}
          subValue={`von ${fmtNum(productsCatalog)} im Katalog`}
          spark={SPARK_PRODUCTS}
          trend={{ delta: '+8,1 %', dir: 'up' }}
          period="vs. Vorperiode"
          loading={loading}
        />
        <KpiCard
          label="Stimmungsbarometer"
          value={`${moodScore >= 0 ? '+' : ''}${moodScore}`}
          subValue={moodLabel}
          spark={SPARK_MOOD}
          trend={{ delta: '+4 Pkt', dir: 'up' }}
          period="vs. Vorperiode"
          loading={loading}
        />
        <KpiCard
          label="E-Mail-Conversion"
          value={fmtPct(emailConvRate)}
          subValue={`${fmtNum(total)} Reviews aus ${fmtNum(emailsSent)} E-Mails`}
          spark={SPARK_CONV}
          trend={{ delta: '+0,8 pp', dir: 'up' }}
          period="vs. Vorperiode"
          loading={loading}
        />
        <KpiCard
          label="Produkte at Risk"
          value={fmtNum(riskCount)}
          subValue="Reviews ≤ 2 ★"
          trend={{ delta: '-3,2 %', dir: 'up' }}
          period="vs. Vorperiode"
          clickable
          onClick={() => navigate('/reviews')}
          loading={loading}
        />
      </div>

      <div className="chart-grid">
        <div className="card span-8">
          <div className="card-head">
            <div>
              <div className="card-title">Sterne-Verteilung</div>
              <div className="card-sub">Anteil pro Bewertungsstufe · {fmtNum(total)} Reviews</div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {dist.map((d) => (
              <div key={d.stars} style={{ display: 'grid', gridTemplateColumns: '32px 1fr 80px 60px', alignItems: 'center', gap: 12 }}>
                <span style={{ font: '600 13px/18px var(--font-body)', color: 'var(--fg-1)' }}>{d.stars}★</span>
                <span style={{ background: '#F2F4F6', borderRadius: 9999, height: 12, position: 'relative', overflow: 'hidden' }}>
                  <span
                    style={{
                      display: 'block',
                      height: '100%',
                      width: `${(d.pct * 100).toFixed(1)}%`,
                      background: ratingColor(d.stars),
                      borderRadius: 9999,
                      transition: 'width 200ms',
                    }}
                  />
                </span>
                <span className="mono" style={{ color: 'var(--fg-2)', textAlign: 'right' }}>{fmtNum(d.count)}</span>
                <span className="mono" style={{ color: 'var(--fg-3)', textAlign: 'right' }}>{fmtPct(d.pct)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card span-4">
          <div className="card-head">
            <div>
              <div className="card-title">Moderation</div>
              <div className="card-sub">Aktueller Status</div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <ModRow label="Pending" count={pending} color="var(--brand-orange)" total={total} />
            <ModRow label="Published" count={published} color="var(--brand-green)" total={total} />
            <ModRow label="Hidden" count={hidden} color="var(--fg-3)" total={total} />
          </div>
        </div>

        <div className="card span-12">
          <div className="card-head">
            <div>
              <div className="card-title">Sentiment-Trend</div>
              <div className="card-sub">12-Monats-Verlauf · positive vs. negative Reviews</div>
            </div>
            <div style={{ display: 'inline-flex', gap: 16, font: '500 12px/16px var(--font-body)', color: 'var(--fg-2)' }}>
              <Legend color="var(--brand-green)" label="Positiv (4–5★)" />
              <Legend color="var(--fg-3)" label="Neutral (3★)" />
              <Legend color="var(--brand-red)" label="Negativ (1–2★)" />
            </div>
          </div>
          <SentimentTrend />
        </div>
      </div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  subValue,
  spark,
  trend,
  period,
  clickable,
  onClick,
  loading,
}: {
  label: string;
  value: string;
  subValue?: string;
  spark?: readonly number[];
  trend?: { delta: string; dir: 'up' | 'down' };
  period?: string;
  clickable?: boolean;
  onClick?: () => void;
  loading?: boolean;
}) {
  return (
    <div
      className={`kpi${clickable ? ' clickable' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) onClick();
      }}
    >
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{loading ? '…' : value}</div>
      {subValue && <div className="kpi-sub">{subValue}</div>}
      {(trend || period) && (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 'auto' }}>
          {trend && (
            <span className={`kpi-trend ${trend.dir}`}>
              <span className="material-symbols-rounded">
                {trend.dir === 'up' ? 'north_east' : 'south_east'}
              </span>
              {trend.delta}
            </span>
          )}
          {period && <span className="kpi-period">{period}</span>}
        </div>
      )}
      {spark && spark.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <Sparkline data={spark} width={140} height={28} />
        </div>
      )}
    </div>
  );
}

function ModRow({ label, count, color, total }: { label: string; count: number; color: string; total: number }) {
  const pct = total > 0 ? count / total : 0;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', font: '500 12px/16px var(--font-body)', color: 'var(--fg-2)', marginBottom: 6 }}>
        <span>{label}</span>
        <span className="mono" style={{ color: 'var(--fg-1)' }}>{fmtNum(count)} · {fmtPct(pct)}</span>
      </div>
      <span style={{ display: 'block', background: '#F2F4F6', borderRadius: 9999, height: 8, overflow: 'hidden' }}>
        <span
          style={{
            display: 'block',
            height: '100%',
            width: `${(pct * 100).toFixed(1)}%`,
            background: color,
            borderRadius: 9999,
          }}
        />
      </span>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 8, height: 8, borderRadius: 999, background: color }} aria-hidden="true" />
      {label}
    </span>
  );
}

function SentimentTrend() {
  // Inline SVG line chart using mock data — three series over 12 months.
  const months = ['Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez', 'Jan', 'Feb', 'Mär', 'Apr'];
  const positive = [62, 64, 63, 66, 68, 67, 70, 72, 71, 73, 74, 76];
  const neutral = [22, 21, 22, 21, 20, 20, 19, 19, 19, 18, 18, 17];
  const negative = [16, 15, 15, 13, 12, 13, 11, 9, 10, 9, 8, 7];
  const W = 920;
  const H = 220;
  const pad = { l: 36, r: 12, t: 12, b: 24 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;

  const xFor = (i: number) => pad.l + (i / (months.length - 1)) * innerW;
  const yFor = (v: number) => pad.t + (1 - v / 100) * innerH;

  function path(values: number[]): string {
    return values.map((v, i) => `${i === 0 ? 'M' : 'L'}${xFor(i).toFixed(1)} ${yFor(v).toFixed(1)}`).join(' ');
  }

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label="Sentiment-Trend">
      {[0, 25, 50, 75, 100].map((g) => (
        <g key={g}>
          <line x1={pad.l} x2={W - pad.r} y1={yFor(g)} y2={yFor(g)} stroke="var(--border-1)" strokeDasharray="3 3" />
          <text x={pad.l - 6} y={yFor(g) + 4} textAnchor="end" fontSize="10" fill="var(--fg-3)" fontFamily="JetBrains Mono">
            {g}%
          </text>
        </g>
      ))}
      <path d={path(positive)} stroke="var(--brand-green)" strokeWidth={2} fill="none" />
      <path d={path(neutral)} stroke="var(--fg-3)" strokeWidth={1.5} fill="none" />
      <path d={path(negative)} stroke="var(--brand-red)" strokeWidth={2} fill="none" />
      {months.map((m, i) => (
        <text
          key={m}
          x={xFor(i)}
          y={H - 6}
          textAnchor="middle"
          fontSize="11"
          fill="var(--fg-3)"
          fontFamily="Inter"
        >
          {m}
        </text>
      ))}
    </svg>
  );
}

function ratingColor(stars: number): string {
  switch (stars) {
    case 5: return 'var(--brand-green)';
    case 4: return 'var(--brand-teal)';
    case 3: return 'var(--brand-cyan)';
    case 2: return 'var(--brand-orange)';
    case 1: return 'var(--brand-red)';
    default: return 'var(--fg-3)';
  }
}
